"use client";
import React, { useState, useEffect } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";
import { deals } from "@/lib/data";

function Countdown({ expiryDate }: { expiryDate: string }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, mins: 0 });

  useEffect(() => {
    const calc = () => {
      const diff = new Date(expiryDate).getTime() - Date.now();
      if (diff <= 0) return setTimeLeft({ days: 0, hours: 0, mins: 0 });
      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      setTimeLeft({ days, hours, mins });
    };
    calc();
    const id = setInterval(calc, 60000);
    return () => clearInterval(id);
  }, [expiryDate]);

  return (
    <div className="flex items-center gap-1.5 text-xs font-bold text-red-500">
      <Icon name="ClockIcon" size={12} />
      {timeLeft.days > 0 ? `Son ${timeLeft.days} gün` : `${timeLeft.hours}s ${timeLeft.mins}dk`}
    </div>
  );
}

export default function DealsSection() {
  const [revealed, setRevealed] = useState<string[]>([]);

  const revealCoupon = (id: string) => {
    setRevealed((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  return (
    <section className="py-16 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-section-title text-foreground">
              Bugünün En İyi Fırsatları
            </h2>
          </div>
          <Link href="/directory" className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            Tüm Fırsatlar
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {deals.map((deal) => (
            <div
              key={deal.id}
              className="bg-white rounded-2xl overflow-hidden border border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <AppImage
                  src={deal.image}
                  alt={`${deal.title} — ${deal.businessName} kampanyası, ${deal.discount} indirim`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
                {/* Discount Badge */}
                <div className="absolute top-3 left-3">
                  <span className="px-3 py-1.5 rounded-full text-sm font-black text-white gradient-primary shadow-lg">
                    {deal.discount} İndirim
                  </span>
                </div>
                <div className="absolute bottom-3 right-3">
                  <Countdown expiryDate={deal.expiryDate} />
                </div>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-1">
                <p className="text-xs font-semibold text-primary mb-1">{deal.businessName}</p>
                <h3 className="font-bold text-base text-foreground mb-2 line-clamp-2">{deal.title}</h3>
                <p className="text-xs text-muted-foreground mb-4 line-clamp-2 flex-1">{deal.description}</p>

                {/* Prices */}
                {deal.originalPrice && (
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm line-through text-muted-foreground">{deal.originalPrice}</span>
                    <span className="text-lg font-black text-primary">{deal.discountedPrice}</span>
                  </div>
                )}

                {/* Coupon Button */}
                <button
                  onClick={() => revealCoupon(deal.id)}
                  className={`w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                    revealed.includes(deal.id)
                      ? "bg-green-50 border-2 border-dashed border-green-400 text-green-700 tracking-widest" :"btn-primary"
                  }`}
                >
                  {revealed.includes(deal.id) ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="ClipboardDocumentIcon" size={16} />
                      {deal.couponCode}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="TagIcon" size={16} />
                      Kuponu Göster
                    </span>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}